/* MyEbike shop open/closed state — shared logic.
   Reads each [data-mb-hours-row]'s data-* attributes (weekday, open, close,
   closed-all-day), compares against the current Brisbane time (or whatever
   timezone the container is set to), renders a status pill, and (if a
   sibling hours list exists) highlights today's row. Auto-refreshes every 60s.

   Used by:
   - sections/myebike-shop-locator.liquid (full table + status pill)
   - snippets/mb-shop-block.liquid (status pill only, in footer)

   The status pill is `[data-mb-hours-status]`. Hours rows are
   `[data-mb-hours-row]` inside the SAME container as the pill (compact
   variant), or inside a sibling `[data-mb-hours-list]` (full variant).

   Cache-safe: all computation happens client-side against the visitor's
   real wall-clock time, not the Liquid render time. */
(function () {
  if (window.__mbShopOpenStateInit) return;
  window.__mbShopOpenStateInit = true;

  function init() {
    const containers = document.querySelectorAll('[data-mb-hours-status]');
    if (!containers.length) return;

    function nowInTz(tz) {
      try {
        // en-GB gives 24h time; weekday: 'short' returns Mon/Tue/etc.
        const fmt = new Intl.DateTimeFormat('en-GB', {
          timeZone: tz, hour12: false,
          weekday: 'short', hour: '2-digit', minute: '2-digit'
        });
        const parts = Object.fromEntries(fmt.formatToParts(new Date()).map(p => [p.type, p.value]));
        const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
        return {
          weekday: dayMap[parts.weekday] ?? new Date().getDay(),
          minutes: (parseInt(parts.hour, 10) * 60) + parseInt(parts.minute, 10)
        };
      } catch (e) {
        const d = new Date();
        return { weekday: d.getDay(), minutes: d.getHours() * 60 + d.getMinutes() };
      }
    }

    function parseHHMM(str) {
      if (!str) return null;
      const m = String(str).match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return null;
      return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    }

    function fmtTime(mins) {
      const h24 = Math.floor(mins / 60);
      const m = mins % 60;
      const ampm = h24 >= 12 ? 'pm' : 'am';
      const h12 = ((h24 + 11) % 12) + 1;
      return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
    }

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    function findRowsFor(container) {
      // Prefer a dedicated sibling list (full variant — section).
      // Fall back to rows nested inside the container itself (compact variant — footer).
      const parent = container.parentElement;
      const sibling = parent ? parent.querySelector('[data-mb-hours-list]') : null;
      const scope = sibling || container;
      return Array.from(scope.querySelectorAll('[data-mb-hours-row]'));
    }

    function update(container) {
      const tz = container.dataset.tz || 'Australia/Brisbane';
      const rows = findRowsFor(container);
      if (!rows.length) {
        // No schedule data — hide the pill rather than mislead.
        container.style.display = 'none';
        return;
      }

      const schedule = {};
      rows.forEach(r => {
        const wd = parseInt(r.dataset.weekday, 10);
        if (Number.isNaN(wd)) return;
        schedule[wd] = {
          open: parseHHMM(r.dataset.open),
          close: parseHHMM(r.dataset.close),
          closedAllDay: r.dataset.closedAllDay === '1',
          row: r
        };
      });

      if (Object.keys(schedule).length === 0) {
        container.style.display = 'none';
        return;
      }
      container.style.display = '';

      const { weekday, minutes } = nowInTz(tz);

      // Highlight today's row (only meaningful when rows are visible — full variant).
      rows.forEach(r => r.classList.remove('is-today'));
      if (schedule[weekday]) schedule[weekday].row.classList.add('is-today');

      const today = schedule[weekday];
      const text = container.querySelector('[data-mb-hours-status-text]')
                || container.querySelector('.mb-hours-status__text')
                || container.querySelector('.mb-shopblock__hours-text');
      container.classList.remove('is-open', 'is-closed');

      if (!text) return;

      if (today && !today.closedAllDay && today.open != null && today.close != null
          && minutes >= today.open && minutes < today.close) {
        container.classList.add('is-open');
        text.textContent = `Open now · closes ${fmtTime(today.close)}`;
        return;
      }

      container.classList.add('is-closed');
      if (today && !today.closedAllDay && today.open != null && minutes < today.open) {
        text.textContent = `Closed · opens ${fmtTime(today.open)}`;
        return;
      }
      for (let i = 1; i <= 7; i++) {
        const wd = (weekday + i) % 7;
        const d = schedule[wd];
        if (d && !d.closedAllDay && d.open != null) {
          const dayLabel = i === 1 ? 'tomorrow' : dayNames[wd];
          text.textContent = `Closed · opens ${dayLabel} ${fmtTime(d.open)}`;
          return;
        }
      }
      text.textContent = 'Closed';
    }

    function tick() { containers.forEach(update); }
    tick();
    setInterval(tick, 60 * 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
